import { cn } from '../../shared/lib/cn';
import { pointTime } from '../../shared/lib/format';
import { CheckIcon, CloseIcon, MoreIcon, SpinnerIcon } from '../../shared/ui/icons';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../shared/ui/menu';
import type { IColumn } from '../../shared/ui/table';
import {
  appOf,
  kindLabel,
  secretHint,
  secretWordOf,
  usageLine,
  type TConnector,
  type TCredential,
} from '../../entities/credential';

const LastCheck = ({
  credential,
  checking,
  now,
  timeZone,
}: {
  credential: TCredential;
  checking: boolean;
  now: number;
  timeZone?: string;
}) => {
  if (checking) {
    return (
      <span className="flex items-center gap-2 text-muted">
        <SpinnerIcon size={12} /> checking
      </span>
    );
  }
  if (credential.lastCheckOk === null || !credential.lastCheckAt) {
    return <span className="text-faint">not checked yet</span>;
  }
  const at = pointTime(credential.lastCheckAt, now, timeZone);
  const ok = credential.lastCheckOk;
  const text = `${ok ? 'works' : (credential.lastCheckDetail ?? 'failed')} · ${at}`;
  return (
    <span
      title={text}
      className={cn('flex min-w-0 items-center gap-2', ok ? 'text-primary' : 'text-danger-text')}
    >
      <span
        aria-hidden
        className={cn(
          'flex size-check shrink-0 items-center justify-center rounded-full text-surface',
          ok ? 'bg-success' : 'bg-danger',
        )}
      >
        {ok ? <CheckIcon size={10} /> : <CloseIcon size={10} />}
      </span>
      <span className="truncate">{text}</span>
    </span>
  );
};

export interface ICredentialColumnsArgs {
  connectors: readonly TConnector[];
  editable: boolean;
  checkingId: string | null;
  now: number;
  timeZone?: string;
  onCheck: (credential: TCredential) => void;
  onReplace: (credential: TCredential) => void;
  onUsed: (credential: TCredential) => void;
  onRemove: (credential: TCredential) => void;
}

export const credentialColumns = ({
  connectors,
  editable,
  checkingId,
  now,
  timeZone,
  onCheck,
  onReplace,
  onUsed,
  onRemove,
}: ICredentialColumnsArgs): IColumn<TCredential>[] => [
  {
    key: 'name',
    header: 'Name',
    width: 'grow',
    ui: true,
    render: (credential) => (
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-primary">{credential.name}</span>
        <span className="truncate text-meta text-muted">{kindLabel(credential, connectors)}</span>
      </span>
    ),
  },
  {
    key: 'secret',
    header: 'Secret',
    width: 'w-col-source',
    render: (credential) => <span className="text-meta text-muted">{secretHint(credential)}</span>,
  },
  {
    key: 'check',
    header: 'Last check',
    width: 'w-col-schedule',
    ui: true,
    render: (credential) => (
      <LastCheck
        credential={credential}
        checking={checkingId === credential.id}
        now={now}
        timeZone={timeZone}
      />
    ),
  },
  {
    key: 'used',
    header: 'Used in',
    width: 'w-col-verdict',
    ui: true,
    render: (credential) => {
      if (credential.usage.steps === 0) return <span className="text-muted">nothing yet</span>;
      const failing = credential.lastCheckOk === false;
      return (
        <button
          type="button"
          onClick={() => onUsed(credential)}
          className={cn(
            'truncate text-left hover:underline',
            failing ? 'text-danger-text' : 'text-primary',
          )}
        >
          {usageLine(credential.usage)}
          {failing && ' — failing'}
        </button>
      );
    },
  },
  {
    key: 'menu',
    header: '',
    width: 'w-col-row-menu',
    ui: true,
    render: (credential) => {
      const app = appOf(credential);
      return (
        <span className="flex justify-end">
          <Menu>
            <MenuTrigger
              aria-label={`More for ${credential.name}`}
              className="flex size-control-xs items-center justify-center rounded-item text-muted hover:bg-ghost hover:text-primary"
            >
              <MoreIcon size={14} />
            </MenuTrigger>
            <MenuContent align="end">
              {editable && <MenuItem onSelect={() => onCheck(credential)}>Check now</MenuItem>}
              {editable && app && (
                <MenuItem onSelect={() => onReplace(credential)}>
                  Replace the {secretWordOf(app, credential.variant)}
                </MenuItem>
              )}
              <MenuItem onSelect={() => onUsed(credential)}>Where it’s used</MenuItem>
              {editable && (
                <MenuItem
                  onSelect={() => onRemove(credential)}
                  tone="danger"
                  description="Only when no step uses it"
                >
                  Remove
                </MenuItem>
              )}
            </MenuContent>
          </Menu>
        </span>
      );
    },
  },
];
